import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Upload, LogOut, FileSpreadsheet, Eye, EyeOff, RefreshCw, LayoutDashboard, CheckSquare, FileText, Search, Database, DollarSign, FolderKanban, Lightbulb, BarChart3, ClipboardList, MessageSquare, Image, FolderOpen, Activity } from 'lucide-react';
import StockTable from './StockTable';
import Orders from './Orders';
import Dashboard from './Dashboard';
import ToDo from './ToDo';
import Accounts from './Accounts';
import ChatWindow from './ChatWindow';
import PriceList from './PriceList';
import NewProjects from './NewProjects';
import ProductPlanning from './ProductPlanning';
import Analytics from './Analytics';
import Messaging from './Messaging';
import MediaLibrary from './MediaLibrary';
import DocumentLibrary from './DocumentLibrary';
import ActivityFeed from './ActivityFeed';
import MobileBottomNav from './MobileBottomNav';
import PullToRefresh from './PullToRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WINLIFEDashboard = ({ user, onLogout }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [mergedStocks, setMergedStocks] = useState([]);
  const [showCost, setShowCost] = useState(false);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload', 'view', 'orders', 'merged', etc.
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Helper function to check if user has access to a permission-based panel
  const hasAccess = (panel) => {
    // Check user permissions
    return user.permissions?.[panel] !== false;
  };

  const fetchStocks = async (retryCount = 0) => {
    setLoading(true);
    try {
      console.log('Fetching WINLIFE stocks, attempt:', retryCount + 1);
      const response = await axios.get(`${API}/stock/winlife`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      setStocks(response.data);
      console.log('WINLIFE stocks fetched successfully:', response.data.length, 'items');
      
      if (response.data.length === 0 && retryCount === 0) {
        // If no data, try once more after a brief delay
        setTimeout(() => fetchStocks(1), 1000);
      }
    } catch (error) {
      console.error('Error fetching WINLIFE stocks:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      if (retryCount === 0) {
        // Retry once after 2 seconds
        console.log('Retrying in 2 seconds...');
        setTimeout(() => fetchStocks(1), 2000);
      } else {
        // Show error after retry
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to fetch stock data';
        toast.error(`Error: ${errorMsg}. Please try refreshing or contact support.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch merged stocks (permission-based)
  const fetchMergedStocks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/merged`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setMergedStocks(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch merged stock data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'view') {
      console.log('Switching to view tab, current stocks:', stocks.length);
      console.log('Fetching WINLIFE stocks...');
      fetchStocks();
    }
    if (activeTab === 'merged' && hasAccess('stock')) {
      fetchMergedStocks();
    }
  }, [activeTab]);

  // Pull-to-refresh handler + event listener for child components
  const handleMobileRefresh = async () => {
    try {
      if (activeTab === 'view') await fetchStocks();
      else if (activeTab === 'merged') await fetchMergedStocks();
      else {
        window.dispatchEvent(new CustomEvent('app:refresh-data', { detail: { tab: activeTab } }));
        await new Promise((res) => setTimeout(res, 600));
      }
    } catch (e) { /* ignore */ }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      
      if (validExtensions.includes(fileExtension)) {
        setFile(selectedFile);
        toast.success(`File "${selectedFile.name}" selected`);
      } else {
        toast.error('Please select a valid file (.xlsx, .xls, or .csv)');
        e.target.value = null;
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/winlife`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      toast.success(response.data.message + ' - Fetching your data...');
      setFile(null);
      if (document.getElementById('file-input')) {
        document.getElementById('file-input').value = null;
      }
      
      // Wait a bit for data to be fully processed
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Switch to view mode first
      setActiveTab('view');
      
      // Then fetch the uploaded data (useEffect will also trigger this)
      await fetchStocks();
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      toast.error(`Upload error: ${errorMsg}`);
      console.error('Upload error details:', error);
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    { key: 'product_name', label: 'Product Name', sortable: true, type: 'text', minWidth: '150px' },
    { key: 'packing', label: 'Packing', sortable: true, type: 'text', minWidth: '100px' },
    { key: 'available_qty', label: 'Available Qty', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'manufactured_by', label: 'Manufactured By', sortable: true, type: 'text', minWidth: '130px' },
    { key: 'order_given_to', label: 'Order Given To', sortable: true, type: 'text', minWidth: '130px' },
    { key: 'order_qty', label: 'Order Qty', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'on_date', label: 'On Date', sortable: true, type: 'text', minWidth: '100px' },
    { key: 'cost', label: 'Cost @ WINLIFE', sortable: true, type: 'number', minWidth: '110px', hidden: !showCost },
  ];

  // Merged stock columns (for permission-based merged stock view)
  const mergedColumns = [
    { key: 'product_name', label: 'Product Name', sortable: true, type: 'text', minWidth: '150px' },
    { key: 'qty_srh', label: 'Qty @ SRH', sortable: true, type: 'number', minWidth: '90px' },
    { key: 'qty_winlife', label: 'Qty @ WINLIFE', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'merged_quantity', label: 'Merged Qty', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'sale_qty_last_month', label: 'Sale (Last Month)', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'available_qty_months', label: 'Available Qty (Months)', sortable: true, type: 'number', minWidth: '110px', format: (val) => val ? val.toFixed(2) : '0.00' },
    { key: 'manufactured_by', label: 'Manufactured By', sortable: true, type: 'text', minWidth: '130px' },
    { key: 'expiry_date', label: 'Expiry Date', sortable: true, type: 'text', minWidth: '100px' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg flex items-center justify-center shadow-md">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Stock Management</h1>
              <p className="text-xs text-slate-500">WINLIFE Portal</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex bg-slate-100 rounded-lg p-1 overflow-x-auto">
              <Button
                onClick={() => setActiveTab('dashboard')}
                variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'dashboard' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <LayoutDashboard className="w-4 h-4 mr-1.5" />
                Dashboard
              </Button>
              <Button
                data-testid="upload-tab"
                onClick={() => setActiveTab('upload')}
                variant={activeTab === 'upload' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'upload' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <Upload className="w-4 h-4 mr-1.5" />
                Upload
              </Button>
              <Button
                data-testid="view-tab"
                onClick={() => setActiveTab('view')}
                variant={activeTab === 'view' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'view' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                View Sheet
              </Button>
              {/* Permission-based: Merged Stock */}
              {hasAccess('stock') && (
                <Button
                  onClick={() => setActiveTab('merged')}
                  variant={activeTab === 'merged' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'merged' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Database className="w-4 h-4 mr-1.5" />
                  Merged Stock
                </Button>
              )}
              <Button
                data-testid="orders-tab"
                onClick={() => setActiveTab('orders')}
                variant={activeTab === 'orders' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'orders' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <ClipboardList className="w-4 h-4 mr-1.5" />
                Orders
              </Button>
              <Button
                onClick={() => setActiveTab('todo')}
                variant={activeTab === 'todo' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'todo' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <CheckSquare className="w-4 h-4 mr-1.5" />
                To-Do
              </Button>
              <Button
                onClick={() => setActiveTab('accounts')}
                variant={activeTab === 'accounts' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'accounts' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
              >
                <FileText className="w-4 h-4 mr-1.5" />
                Accounts
              </Button>
              {/* Permission-based: Price List */}
              {hasAccess('pricelist') && (
                <Button
                  onClick={() => setActiveTab('pricelist')}
                  variant={activeTab === 'pricelist' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'pricelist' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <DollarSign className="w-4 h-4 mr-1.5" />
                  Price List
                </Button>
              )}
              {/* Permission-based: Projects */}
              {hasAccess('projects') && (
                <Button
                  onClick={() => setActiveTab('projects')}
                  variant={activeTab === 'projects' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'projects' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <FolderKanban className="w-4 h-4 mr-1.5" />
                  Projects
                </Button>
              )}
              {/* Permission-based: Planning */}
              {hasAccess('product_planning') && (
                <Button
                  onClick={() => setActiveTab('planning')}
                  variant={activeTab === 'planning' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'planning' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Lightbulb className="w-4 h-4 mr-1.5" />
                  Planning
                </Button>
              )}
              {/* Permission-based: Analytics */}
              {hasAccess('analytics') && (
                <Button
                  onClick={() => setActiveTab('analytics')}
                  variant={activeTab === 'analytics' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'analytics' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  Analytics
                </Button>
              )}
              {/* Permission-based: Messaging */}
              {hasAccess('messaging') && (
                <Button
                  onClick={() => setActiveTab('messaging')}
                  variant={activeTab === 'messaging' ? 'default' : 'ghost'}
                  size="sm"
                  data-testid="messaging-tab"
                  className={activeTab === 'messaging' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <MessageSquare className="w-4 h-4 mr-1.5" />
                  Messaging
                </Button>
              )}
              {/* Permission-based: Media Library */}
              {hasAccess('media') && (
                <Button
                  onClick={() => setActiveTab('media')}
                  variant={activeTab === 'media' ? 'default' : 'ghost'}
                  size="sm"
                  data-testid="media-tab"
                  className={activeTab === 'media' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Image className="w-4 h-4 mr-1.5" />
                  Media
                </Button>
              )}
              {/* Permission-based: Documents */}
              {hasAccess('documents') && (
                <Button
                  onClick={() => setActiveTab('documents')}
                  variant={activeTab === 'documents' ? 'default' : 'ghost'}
                  size="sm"
                  data-testid="documents-tab"
                  className={activeTab === 'documents' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <FolderOpen className="w-4 h-4 mr-1.5" />
                  Documents
                </Button>
              )}
              {/* Operators: Activity Feed */}
              {(user.role === 'admin' || user.role === 'winlife') && (
                <Button
                  onClick={() => setActiveTab('activity')}
                  variant={activeTab === 'activity' ? 'default' : 'ghost'}
                  size="sm"
                  data-testid="activity-tab"
                  className={activeTab === 'activity' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Activity className="w-4 h-4 mr-1.5" />
                  Activity
                </Button>
              )}
            </div>
            <span className="text-sm font-semibold text-slate-700" style={{ fontSize: '15px' }}>{user.username}</span>
            <Button
              data-testid="logout-button"
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="border-slate-300 hover:bg-slate-50"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <PullToRefresh onRefresh={handleMobileRefresh}>
      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Dashboard</h2>
              <p className="text-indigo-100 mt-1">Overview of key metrics and insights</p>
            </div>
            <div className="p-6">
              <Dashboard user={user} />
            </div>
          </div>
        ) : activeTab === 'orders' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Orders Management</h2>
              <p className="text-indigo-100 mt-1">Track and manage your product orders</p>
            </div>
            <div className="p-6">
              <Orders user={user} />
            </div>
          </div>
        ) : activeTab === 'todo' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-teal-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>To-Do List</h2>
              <p className="text-slate-300 mt-1">Manage tasks and assignments</p>
            </div>
            <div className="p-6">
              <ToDo user={user} />
            </div>
          </div>
        ) : activeTab === 'accounts' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Accounts</h2>
              <p className="text-slate-300 mt-1">Bank statements and transaction ledger</p>
            </div>
            <div className="p-6">
              <Accounts user={user} />
            </div>
          </div>
        ) : activeTab === 'upload' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Upload Section */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload Stock Sheet</h2>
              <p className="text-indigo-100 mt-1">Upload your inventory file with product details</p>
            </div>

            <div className="p-8">
              {/* File Format Info */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-8">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Required Column Headers
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Product Name</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Packing</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Available Qty</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Manufactured By</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Order Given To</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Order Qty</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">On Date</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                    <span className="text-slate-700">Cost (Optional)</span>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-3">Supported formats: .xlsx, .xls, .csv</p>
              </div>

              {/* Download Template Button */}
              <div className="mb-6">
                <Button
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/template/download/winlife`, {
                        headers: { 'Authorization': `Bearer ${user.token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'WINLIFE_Stock_Upload_Template.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Template downloaded successfully');
                    } catch (error) {
                      toast.error('Failed to download template');
                    }
                  }}
                  variant="outline"
                  className="w-full h-10 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Sample Excel Template
                </Button>
              </div>

              {/* Upload Controls */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="file-input" className="block text-sm font-medium text-slate-700 mb-2">
                    Select File
                  </label>
                  <input
                    id="file-input"
                    data-testid="file-input"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-slate-600 file:mr-4 file:py-2.5 file:px-5 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 file:cursor-pointer cursor-pointer border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {file && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FileSpreadsheet className="w-10 h-10 text-indigo-600 mr-3" />
                      <div>
                        <p className="font-medium text-slate-800">{file.name}</p>
                        <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  data-testid="upload-button"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white font-semibold rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Upload Stock Sheet
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : activeTab === 'view' ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* View Section */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Your Stock Sheet</h2>
                <p className="text-indigo-100 mt-1">View and analyze your uploaded inventory</p>
              </div>
              <div className="flex space-x-3">
                <Button
                  data-testid="toggle-cost-button"
                  onClick={() => setShowCost(!showCost)}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  size="sm"
                >
                  {showCost ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
                  {showCost ? 'Hide' : 'Show'} Cost
                </Button>
                <Button
                  data-testid="refresh-stocks-button"
                  onClick={fetchStocks}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Refresh
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-slate-600">Loading your stock data...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="px-8 py-4 border-b border-slate-200">
                  <Input
                    icon={<Search className="w-4 h-4" />}
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-md"
                  />
                </div>
                <StockTable data={stocks} columns={columns} searchTerm={searchTerm} />
              </>
            )}
          </div>
        ) : activeTab === 'merged' && hasAccess('stock') ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Merged Stock Overview</h2>
                <p className="text-slate-300 mt-1">Combined inventory from all locations</p>
              </div>
              <Button onClick={fetchMergedStocks} variant="outline" className="border-white/30 text-white hover:bg-white/10" size="sm">
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Refresh
              </Button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <svg className="animate-spin h-12 w-12 text-slate-600 mx-auto" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <StockTable data={mergedStocks} columns={mergedColumns} />
            )}
          </div>
        ) : activeTab === 'pricelist' && hasAccess('pricelist') ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-6">
              <PriceList user={user} />
            </div>
          </div>
        ) : activeTab === 'projects' && hasAccess('projects') ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-6">
              <NewProjects user={user} />
            </div>
          </div>
        ) : activeTab === 'planning' && hasAccess('product_planning') ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Product Planning</h2>
              <p className="text-slate-300 mt-1">Plan new products with detailed specifications</p>
            </div>
            <div className="p-6">
              <ProductPlanning user={user} />
            </div>
          </div>
        ) : activeTab === 'analytics' && hasAccess('analytics') ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden p-6">
            <Analytics user={user} onBackToDashboard={() => setActiveTab('dashboard')} />
          </div>
        ) : activeTab === 'messaging' && hasAccess('messaging') ? (
          <Messaging user={user} onBack={() => setActiveTab('dashboard')} />
        ) : activeTab === 'media' && hasAccess('media') ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <MediaLibrary user={user} />
          </div>
        ) : activeTab === 'documents' && hasAccess('documents') ? (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <DocumentLibrary user={user} />
          </div>
        ) : activeTab === 'activity' && (user.role === 'admin' || user.role === 'winlife') ? (
          <ActivityFeed user={user} />
        ) : null}
      </main>
      </PullToRefresh>

      {/* Chat Window */}
      <ChatWindow user={user} />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        hasAccess={hasAccess}
        showMoreMenu={showMoreMenu}
        setShowMoreMenu={setShowMoreMenu}
      />
    </div>
  );
};

export default WINLIFEDashboard;
